export type IParseDate = (
  format: string,
  val: string | IDCA.IGenericObj,
  settings?: IDCA.IGenericObj
) => Date | null;

export function parseDate(
  format: string,
  val: string | IDCA.IGenericObj,
  settings?: IDCA.IGenericObj
): Date | null {
  let value = val;
  if (format === null || value === null) {
    throw Error('Invalid arguments');
  }

  value = typeof value === 'object' ? value.toString() : `${value}`;
  if (value === '') {
    return null;
  }

  // @ts-ignore
  const _defaults = window.DexeroCoreApp.userOptions.datepickerLang[window.language];
  let iFormat: number;
  let dim;
  let extra;
  let iValue = 0;
  const shortYearCutoffTemp =
    (settings ? settings.shortYearCutoff : null) || _defaults.shortYearCutoff;
  const shortYearCutoff =
    typeof shortYearCutoffTemp !== 'string'
      ? shortYearCutoffTemp
      : (new Date().getFullYear() % 100) + parseInt(shortYearCutoffTemp, 10);
  const dayNamesShort = (settings ? settings.dayNamesShort : null) || _defaults.dayNamesShort;
  const dayNames = (settings ? settings.dayNames : null) || _defaults.dayNames;
  const monthNamesShort = (settings ? settings.monthNamesShort : null) || _defaults.monthNamesShort;
  const monthNames = (settings ? settings.monthNames : null) || _defaults.monthNames;
  let year = -1;
  let month = -1;
  let day = -1;
  let doy = -1;
  let literal = false;
  let date;

  //  Check whether a format character is doubled
  function lookAhead(match: string) {
    const matches = iFormat + 1 < format.length && format.charAt(iFormat + 1) === match;
    if (matches) {
      iFormat += 1;
    }
    return matches;
  }

  //  Extract a number from the string value
  function getNumber(match: string): number {
    const isDoubled = lookAhead(match);
    const size =
      match === '@'
        ? 14
        : match === '!'
        ? 20
        : match === 'y' && isDoubled
        ? 4
        : match === 'o'
        ? 3
        : 2;
    const minSize = match === 'y' ? size : 1;
    const digits = new RegExp('^\\d{' + minSize + ',' + size + '}');
    const num = (value as string).substring(iValue).match(digits);
    if (!num) {
      throw Error(`Missing number at position ${iValue}`);
    }
    iValue += num[0].length;
    return parseInt(num[0], 10);
  }

  //  Extract a name from the string value and convert to an index
  function getName(match: string, shortNames: string[], longNames: string[]) {
    let index = -1;
    const names: any[] = $.map(lookAhead(match) ? longNames : shortNames, (v, k) => [[k, v]]).sort(
      (a: any, b: any) => -(a[1].length - b[1].length)
    );

    $.each(names, (i, pair) => {
      const name: string = pair[1];
      if ((value as string).substr(iValue, name.length).toLowerCase() === name.toLowerCase()) {
        index = pair[0];
        iValue += name.length;
        return false;
      }
      return true;
    });
    if (index !== -1) {
      return index + 1;
    }
    throw Error(`Unknown name at position ${iValue}`);
  }

  //  Confirm that a literal character matches the string value
  function checkLiteral() {
    if ((value as string).charAt(iValue) !== format.charAt(iFormat)) {
      throw Error(`Unexpected literal at position ${iValue}`);
    }
    iValue += 1;
  }

  for (iFormat = 0; iFormat < format.length; iFormat += 1) {
    if (literal) {
      if (format.charAt(iFormat) === "'" && !lookAhead("'")) {
        literal = false;
      } else {
        checkLiteral();
      }
    } else {
      switch (format.charAt(iFormat)) {
        case 'd':
          day = getNumber('d');
          break;
        case 'D':
          getName('D', dayNamesShort, dayNames);
          break;
        case 'o':
          doy = getNumber('o');
          break;
        case 'm':
          month = getNumber('m');
          break;
        case 'M':
          month = getName('M', monthNamesShort, monthNames);
          break;
        case 'y':
          year = getNumber('y');
          break;
        case '@':
          date = new Date(getNumber('@'));
          year = date.getFullYear();
          month = date.getMonth() + 1;
          day = date.getDate();
          break;
        case '!':
          date = new Date((getNumber('!') - window.DexeroCoreApp._ticksTo1970()) / 10000);
          year = date.getFullYear();
          month = date.getMonth() + 1;
          day = date.getDate();
          break;
        case "'":
          if (lookAhead("'")) {
            checkLiteral();
          } else {
            literal = true;
          }
          break;
        default:
          checkLiteral();
      }
    }
  }

  if (iValue < value.length) {
    extra = value.substr(iValue);
    if (!/^\s+/.test(extra)) {
      throw Error(`Extra/unparsed characters found in date: ${extra}`);
    }
  }

  if (year === -1) {
    year = new Date().getFullYear();
  } else if (year < 100) {
    year +=
      new Date().getFullYear() -
      (new Date().getFullYear() % 100) +
      (year <= shortYearCutoff ? 0 : -100);
  }

  if (doy > -1) {
    month = 1;
    day = doy;
    do {
      dim = window.DexeroCoreApp.getDaysInMonth(year, month - 1);
      if (day <= dim) {
        break;
      }
      month += 1;
      day -= dim;
    } while (true);
  }

  date = window.DexeroCoreApp._daylightSavingAdjust(new Date(year, month - 1, day));
  if (
    !date ||
    date.getFullYear() !== year ||
    date.getMonth() + 1 !== month ||
    date.getDate() !== day
  ) {
    throw Error('Invalid date'); //  E.g. 31/02/00
  }
  return date;
}
